import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { Input } from '../../../components/input'
import { Select } from '../../../components/select'
import { Textarea } from '../../../components/textarea'
import { Checkbox, CheckboxField, CheckboxGroup } from '../../../components/checkbox'
import { MultiSelect } from '../../../components/multi-select'
import { GET_ROUTES_FOR_REPORTS } from '../../../graphql/reports/queries'
import { GET_COUNTRY_LIST } from '../../../graphql/common/queries'
import { ErrorDialog, SuccessDialog } from '../../../components/custom/ResponseDialog'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { GET_ROUTE_TYPE } from 'graphql/subscription/queries'
import Flatpickr from 'react-flatpickr'
import { GET_UNIPAL_CONFIG } from 'graphql/unipal/queries'
import { CREATE_UNIPAL_CONFIG, UPDATE_UNIPAL_CONFIG } from 'graphql/unipal/mutations'
import { GET_BOAT_DAY_PASS_PRICE } from 'graphql/booking/queries'

const UnipalForm = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditMode = !!id
    const [dayPassPrices, setDayPassPrices] = useState([])
    const [effectiveFrom, setEffectiveFrom] = useState('2025-01-30')
    const [effectiveTo, setEffectiveTo] = useState('2028-01-30')

    const [formData, setFormData] = useState({
        route_type: '',
        route_ids: [],
        daypass_ids: [],
        trip_ids: [],
        total_count: 0,
        effective_from: '',
        effective_to: '',
        status_code: '',
    })

    const [errors, setErrors] = useState({})
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const [routes, setRoutes] = useState([])
    const [countries, setCountries] = useState([])
    const [trips, setTrips] = useState([])
    const [routeTypes, setRouteTypes] = useState([])

    const [getUnipalDetails, { loading: loadingUnipal }] = useLazyQuery(GET_UNIPAL_CONFIG, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getUnipalConfigs?.unipal_configs?.[0]) {
                const Uni = data.getUnipalConfigs.unipal_configs[0]
                setEffectiveFrom(toInputDate(Uni.effective_from))
                setEffectiveTo(toInputDate(Uni.effective_to))
                setFormData({
                    unipal_item_id: Uni.unipal_item_id,
                    route_type: Uni.route_type || '',
                    routes: Uni.routes?.map(r => r.route_id) || [],
                    trips: Uni.trips?.map(t => t.trip_id) || [],
                    effective_from: toInputDate(Uni.effective_from) || '',
                    effective_to: toInputDate(Uni.effective_to) || '',
                    status_code: Uni.status_code || 'ACT',
                    item_type: Uni.item_type || '',
                    daypasses: Uni.daypasses?.map(d => d.daypass_id) || [],
                })
                getUnipalDetails({
                    variables: {
                        filter: {
                            unipal_item_id: id,
                            effective_from: effectiveFrom,
                            effective_to: effectiveTo,
                        },
                        page: {
                            offset: 0,
                            limit: 1,
                        },
                    },
                })
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch Unipal details'))
            setShowErrDialog(true)
        },
    })

    const [getRoutes] = useLazyQuery(GET_ROUTES_FOR_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getBoatRoute?.success && data?.getBoatRoute?.boat_routes) {
                setRoutes(data.getBoatRoute.boat_routes)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch routes'))
            setShowErrDialog(true)
        },
    })

    const [getCountries] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.country) {
                setCountries(data.country)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch countries'))
            setShowErrDialog(true)
        },
    })

    const [getRouteTypes] = useLazyQuery(GET_ROUTE_TYPE, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.routeType) {
                setRouteTypes(data.routeType)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch route types'))
            setShowErrDialog(true)
        },
    })

    const influencerStatuses = [
        { value: 'ACT', description: 'Active' },
        { value: 'INA', description: 'Inactive' },
    ]

    const items = [
        { value: 'TRIP', description: 'trip' },
        { value: 'DAYPASS', description: 'daypass' },
    ]

    const [createUnipal, { loading: createLoading }] = useMutation(CREATE_UNIPAL_CONFIG, {
        onCompleted: data => {
            if (data.createUnipalConfig.success) {
                setSuccessMessage(t('Unipal plan created successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    navigate('/unipal-config')
                }, 2000)
            } else {
                setErrorMessage(data.createUnipalConfig.message || t('Failed to create Unipal plan'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while creating Unipal plan'))
            setShowErrDialog(true)
        },
    })

    const [updateUnipal, { loading: updateLoading }] = useMutation(UPDATE_UNIPAL_CONFIG, {
        onCompleted: data => {
            if (data.updateUnipalConfig.success) {
                setSuccessMessage(t('Unipal items updated successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    navigate('/unipal-config')
                }, 2000)
            } else {
                setErrorMessage(data.updateUnipalConfig.message || t('Failed to update Unipal plan'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while updating Unipal plan'))
            setShowErrDialog(true)
        },
    })

    const [getDayPassPrices, { loading: dayPassLoading }] = useLazyQuery(GET_BOAT_DAY_PASS_PRICE, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getBoatDayPassPrices?.success && data?.getBoatDayPassPrices?.day_pass_prices) {
                // Filter only active day pass prices
                const activePrices = data.getBoatDayPassPrices.day_pass_prices.filter(
                    price => price.day_pass_price_status?.code === 'ACT'
                )
                setDayPassPrices(activePrices)
            }
        },
        onError: error => {
            console.error('Error fetching day pass prices:', error)
            setDayPassPrices([])
        },
    })

    useEffect(() => {
        getCountries({ variables: { id: '' } })
        getRoutes({
            variables: {
                page: { offset: 0, limit: 100 },
                routeId: '',
                routeName: '',
                statusCode: 'ACT',
            },
        })
        getRouteTypes({ variables: { id: '' } })
        getDayPassPrices({
            variables: {
                page: { limit: 100, offset: 0 },
                priceFilter: null,
            },
        })

        // Load Unipal details if in edit mode
        if (isEditMode && id) {
            getUnipalDetails({
                variables: {
                    filter: {
                        unipal_item_id: id,
                        effective_from: '2025-01-01',
                        effective_to: '2030-12-31',
                    },
                    page: {
                        offset: 0,
                        limit: 1,
                    },
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Populate trips based on selected routes
    useEffect(() => {
        if (formData.routes?.length > 0) {
            const selectedRoutes = routes.filter(route => formData.routes.includes(String(route.route_id)))
            const allTrips = selectedRoutes.flatMap(route => route.route_trip || [])
            const uniqueTrips = allTrips.filter(
                (trip, index, self) => index === self.findIndex(t => t.trip_id === trip.trip_id)
            )
            setTrips(uniqueTrips)
        } else {
            setTrips([])
        }
    }, [formData.routes, routes])

    const toInputDate = isoString => {
        return isoString ? isoString.split('T')[0] : ''
    }

    // Helper function to remove __typename from objects
    const removeTypename = obj => {
        if (obj === null || obj === undefined) return obj
        if (Array.isArray(obj)) return obj.map(removeTypename)
        if (typeof obj === 'object') {
            const { __typename, ...rest } = obj
            return Object.keys(rest).reduce((acc, key) => {
                acc[key] = removeTypename(rest[key])
                return acc
            }, {})
        }
        return obj
    }

    const validateForm = () => {
        const newErrors = {}

        if (formData.item_type === 'TRIP') {
            if (!formData.routes || formData.routes.length === 0) {
                newErrors.routes = t('Please select at least one route')
            }

            if (!formData.trips || formData.trips.length === 0) {
                newErrors.trips = t('Please select at least one trip')
            }
        }
        if (!formData.effective_from) newErrors.effective_from = t('Valid from date is required')
        if (!formData.effective_to) newErrors.effective_to = t('Valid to date is required')
        if (formData.effective_from && formData.effective_to && formData.effective_from >= formData.effective_to) {
            newErrors.effective_to = t('Valid to date must be after valid from date')
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = e => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        // Clean the data by removing __typename fields
        const cleanedData = removeTypename(formData)

        const createInput = {
            route_type: cleanedData.route_type,
            item_type: cleanedData.item_type,
            route_ids: cleanedData.routes,
            trip_ids: cleanedData.trips,
            status_code: cleanedData.status_code,
            effective_from: cleanedData.effective_from,
            effective_to: cleanedData.effective_to,
            daypass_ids: cleanedData.daypasses,
        }

        const updateInput = {
            effective_from: cleanedData.effective_from,
            effective_to: cleanedData.effective_to,
            status_code: cleanedData.status_code,
        }

        if (isEditMode) {
            updateUnipal({ variables: { unipal_item_id: id, unipal_config: updateInput } })
        } else {
            createUnipal({ variables: { unipal_config: createInput } })
        }
    }

    if (loadingUnipal) {
        return (
            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                <img alt="logo" src={loadingImage} className="w-[250px]" />
                <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
            </div>
        )
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/unipal-config')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back to Unipal items')}
                </button>
            </div>

            <div className="max-w-4xl mx-auto mb-8">
                <form onSubmit={handleSubmit}>
                    <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
                        <div className="px-4 py-6 sm:p-8">
                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                                        {isEditMode ? t('Edit Unipal Config') : t('Create Unipal Config')}
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {isEditMode
                                            ? t('Update the unipal plan details')
                                            : t('Add a new unipal plan details')}
                                    </p>
                                </div>
                                <div className=" sm:col-span-6 border-b border-gray-900/10"></div>

                                <div className="sm:col-span-6">
                                    <h2 className="text-lg font-medium text-gray-900">{t('Basic Information')}</h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {t('Enter the basic details of unipal item')}
                                    </p>
                                </div>

                                {/* Basic Information */}
                                {formData.unipal_item_id && (
                                    <div className="sm:col-span-6">
                                        <label className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('Unipal User')}
                                            <span className="text-red">*</span>
                                        </label>
                                        <Input
                                            type="text"
                                            value={formData.unipal_item_id || ''}
                                            onChange={e =>
                                                setFormData({
                                                    ...formData,
                                                    unipal_item_id: e.target.value,
                                                })
                                            }
                                            disabled
                                        />
                                        {errors.Unipal_name && (
                                            <p className="text-red text-xs mt-1">{errors.Unipal_name}</p>
                                        )}
                                    </div>
                                )}
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Item Type')}
                                        <span className="text-red">*</span>
                                    </label>

                                    <Select
                                        value={formData.item_type || ''}
                                        onChange={e => setFormData({ ...formData, item_type: e.target.value })}
                                        required
                                    >
                                        <option value="">{t('Select Item')}</option>

                                        {items.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.description}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                {formData.item_type === 'DAYPASS' && (
                                    <div className="sm:col-span-6">
                                        <label className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('Daypass')}
                                            <span className="text-red">*</span>
                                        </label>
                                        <Select
                                            type="text"
                                            value={formData.daypasses || ''}
                                            onChange={e =>
                                                setFormData({
                                                    ...formData,
                                                    daypasses: e.target.value,
                                                })
                                            }
                                            required
                                        >
                                            <option value="">
                                                {dayPassLoading ? t('Loading...') : t('Select Day Pass')}
                                            </option>
                                            {dayPassPrices
                                                .filter(value => {
                                                    const today = new Date(Date.now())
                                                    const validTo = new Date(value.valid_to)
                                                    return today <= validTo
                                                })
                                                .map(dayPass => (
                                                    <option
                                                        key={dayPass.master_price_id}
                                                        value={dayPass.master_price_id}
                                                    >
                                                        {dayPass.name?.en ||
                                                            dayPass.name?.ar ||
                                                            dayPass.master_price_id}
                                                    </option>
                                                ))}
                                        </Select>
                                        {errors.Unipal_name && (
                                            <p className="text-red text-xs mt-1">{errors.Unipal_name}</p>
                                        )}
                                    </div>
                                )}
                                {/* Validity*/}
                                <div className="sm:col-span-6">
                                    <h2 className="text-lg font-medium text-gray-900 mt-4">{t('Validity')}</h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {t('Pick valid date for unipal item')}
                                    </p>
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Effective From')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Flatpickr
                                        value={formData.effective_from || null}
                                        placeholder="yyyy-mm-dd"
                                        options={{
                                            dateFormat: 'Y-m-d',
                                            minDate: 'today',
                                        }}
                                        onChange={([date]) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                effective_from: date.toISOString().split('T')[0],
                                            }))
                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                        required
                                    />
                                    {errors.effective_from && (
                                        <p className="text-red text-xs mt-1">{errors.effective_from}</p>
                                    )}
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Effective To')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Flatpickr
                                        value={formData.effective_to || null}
                                        placeholder="yyyy-mm-dd"
                                        options={{
                                            dateFormat: 'Y-m-d',
                                            minDate: 'today',
                                        }}
                                        onChange={([date]) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                effective_to: date.toISOString().split('T')[0],
                                            }))
                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                        required
                                    />
                                    {errors.effective_to && (
                                        <p className="text-red text-xs mt-1">{errors.effective_to}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>

                                    <Select
                                        value={formData.status_code || ''}
                                        onChange={e => setFormData({ ...formData, status_code: e.target.value })}
                                        required
                                    >
                                        <option value="">{t('Select Status')}</option>

                                        {influencerStatuses.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.description}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                {/* Eligibility */}
                                <div className="sm:col-span-6">
                                    <h2 className="text-lg font-medium text-gray-900 mt-4">{t('Eligibility')}</h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {t('Enter Route Information for unipal trips')}
                                    </p>
                                </div>

                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Route Category')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.route_type}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                route_type: e.target.value,
                                                routes: [],
                                                trips: [],
                                            })
                                        }
                                        required
                                    >
                                        <option value="">{t('Select Route Category')}</option>
                                        {routeTypes.map(type => (
                                            <option key={type.code} value={type.code}>
                                                {type.description?.en || type.code}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                {formData.item_type === 'TRIP' && (
                                    <div className="sm:col-span-6">
                                        <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                            {t('Routes')}
                                        </label>
                                        <MultiSelect
                                            placeholder={t('Select routes')}
                                            value={formData.routes || []}
                                            onChange={values =>
                                                setFormData({
                                                    ...formData,
                                                    routes: values,
                                                })
                                            }
                                            required
                                            options={routes
                                                .filter(
                                                    route =>
                                                        route.master_route?.route_type?.code ===
                                                        (formData.route_type === 'DMC' ? 'DMC' : 'INL')
                                                )
                                                .map(route => ({
                                                    value: route.route_id,
                                                    label: route.route_name?.en || route.route_name,
                                                }))}
                                            disabled={formData.route_type?.length === 0}
                                        />
                                        {formData.route_type?.length === 0 && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t('Please select route category first to see available routes')}
                                            </p>
                                        )}
                                        {errors.routes && <p className="text-red text-xs mt-1">{errors.routes}</p>}
                                    </div>
                                )}
                                {formData.item_type === 'TRIP' && (
                                    <div className="sm:col-span-6">
                                        <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                            {t('Trips')}
                                        </label>
                                        <MultiSelect
                                            placeholder={t('Select trips')}
                                            value={formData.trips || []}
                                            onChange={values =>
                                                setFormData({
                                                    ...formData,
                                                    trips: values,
                                                })
                                            }
                                            required
                                            options={trips.map(trip => ({
                                                value: trip.trip_id,
                                                label: trip.trip_name?.en || trip.trip_name,
                                            }))}
                                            disabled={
                                                formData.routes?.length === 0 || formData.route_type?.length === 0
                                            }
                                        />
                                        {formData.routes?.length === 0 ||
                                            (formData.route_type?.length === 0 && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {t('Please select routes first to see available trips')}
                                                </p>
                                            ))}
                                        {errors.trips && <p className="text-red text-xs mt-1">{errors.trips}</p>}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
                            <button
                                type="button"
                                onClick={() => navigate('/unipal-config')}
                                className="text-sm font-semibold leading-6 text-gray-900"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={createLoading || updateLoading}
                                className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
                            >
                                {createLoading || updateLoading
                                    ? t('Saving...')
                                    : isEditMode
                                      ? t('Update Item')
                                      : t('Create Item')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            {showSucDialog && (
                <SuccessDialog
                    status={showSucDialog}
                    title={t('Success')}
                    message={successMessage}
                    onClose={() => {
                        setShowSucDialog(false)
                        setSuccessMessage('')
                    }}
                />
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={showErrDialog}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={() => {
                        setShowErrDialog(false)
                        setErrorMessage('')
                    }}
                />
            )}
        </>
    )
}

export default UnipalForm
